import { LitElement, css, html, nothing } from '@lit'
import styles from '@nova/styles'
import { formatPrice } from '@nova/utils'

class NovaFreeShippingThreshold extends LitElement {
  static properties = {
    freeShippingData: {
      type: Object,
      attribute: 'free-shipping-data',
    },
    cartSubtotal: {
      type: Number,
      state: true,
    },
    percentageValue: {
      type: Number,
      state: true,
    },
    freeShippingThreshold: {
      type: Number,
      state: true,
    },
    gwpThreshold: {
      type: Number,
      state: true,
    },
    status: {
      type: Number,
      state: true,
    },
  }

  get caseType() {
    const hasFreeShipping = !!this.freeShippingThreshold;
    const hasGWP = !!this.gwpThreshold;

    if (hasFreeShipping && hasGWP) {
      return this.freeShippingThreshold > this.gwpThreshold
        ? 'freeShippingLarger'
        : this.gwpThreshold > this.freeShippingThreshold
        ? 'gwpLarger' : 'equalThreshold'
    }

    if (hasFreeShipping) return 'freeShippingOnly'
    if (hasGWP) return 'gwpOnly'

    return 'none'
  }

  get largerThreshold() {
    switch (this.caseType) {
      case 'gwpLarger':
      case 'gwpOnly':
      case 'equalThreshold':
        return this.gwpThreshold
      case 'freeShippingLarger':
      case 'freeShippingOnly':
        return this.freeShippingThreshold
      default:
        return this.cartSubtotal || 1
    }
  }

  get smallerThreshold() {
    switch (this.caseType) {
      case 'gwpLarger':
      case 'gwpOnly':
      case 'equalThreshold':
        return this.freeShippingThreshold
      case 'freeShippingLarger':
      case 'freeShippingOnly':
        return this.gwpThreshold
      default:
        return 0
    }
  }

  get percentageValue() {
    const threshold = this.largerThreshold
    return Math.min((this.cartSubtotal / threshold) * 100, 100)
  }

  get percentageValueFreeShipping() {
    if (this.caseType === 'gwpLarger') {
      return (this.freeShippingThreshold / this.gwpThreshold) * 100
    }
    return (this.gwpThreshold / this.freeShippingThreshold) * 100
  }

  constructor() {
    super()
    document.addEventListener(
      'nova:changeQuantityFinished',
      this._handleUpdateProgressBar,
    )
    this.status = 0
    this.cartSubtotal = 0
  }

  connectedCallback() {
    super.connectedCallback()
    this._initializeThresholds()
    this._asyncUpdateCart()
  }

  _initializeThresholds() {
    // Initialize from global variables if available
    if (typeof __NOVA_GLOBAL__ !== 'undefined') {
      this.freeShippingThreshold = __NOVA_GLOBAL__.freeShippingThreshold || 0
      this.gwpThreshold = __NOVA_GLOBAL__.gwpThreshold || 0
    } else {
      this.freeShippingThreshold = 0
      this.gwpThreshold = 0
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('freeShippingData') && this.freeShippingData) {
      // Update thresholds from passed data if available
      if (this.freeShippingData.freeShippingThreshold) {
        this.freeShippingThreshold = this.freeShippingData.freeShippingThreshold
      }
      if (this.freeShippingData.gwpThreshold) {
        this.gwpThreshold = this.freeShippingData.gwpThreshold
      }
    }
  }

  _asyncUpdateCart = async () => {
    try {
      const response = await fetch('/cart.js')
      if (!response.ok) {
        throw new Error('Error fetching cart')
      }
      const data = await response.json()
      this.cartSubtotal = data.items_subtotal_price / 100
    } catch {
      console.error('Error fetching cart')
    }
  }

  _handleUpdateProgressBar = () => {
    this._asyncUpdateCart()
  }

  _calculateFreeShippingRemaining() {
    return formatPrice((this.freeShippingThreshold - this.cartSubtotal) * 100)
  }

  _calculateGWPRemaining() {
    return formatPrice((this.gwpThreshold - this.cartSubtotal) * 100)
  }

  _displayMessage() {
    // This project has two progress bars: one for GWP (Gift With Purchase) and one for Free Shipping.
    //
    // The conditions to consider:
    // 1. Only GWP is available.
    // 2. Only Free Shipping is available.
    // 3. GWP threshold is larger than Free Shipping.
    // 4. Free Shipping threshold is larger than GWP.
    // 5. GWP and Free Shipping have equal thresholds.
    //
    // Progress dot marks:
    // - Display dot marks only when GWP and Free Shipping have different thresholds.
    // - The dot mark for the larger threshold is always positioned at 100%.
    // - The dot mark for the smaller threshold is placed at (smaller threshold / larger threshold) * 100%.
    //
    // State definitions:
    // - FULL: All applicable progress bars are fulfilled.
    //   - If only GWP exists, FULL means GWP is fulfilled.
    //   - If both GWP and Free Shipping exist, FULL means both are fulfilled.
    // - PARTIAL: Only the progress bar with the smaller threshold is fulfilled.
    //   - If only GWP exists, PARTIAL means GWP is not fulfilled.
    //   - If both GWP and Free Shipping exist, PARTIAL means one of them is fulfilled.
    // - ORIGINAL: No progress bars are fulfilled.
    //   - If only GWP exists, ORIGINAL means GWP is not fulfilled.
    //   - If both GWP and Free Shipping exist, ORIGINAL means neither is fulfilled.
    //
    // Message Templates:
    // - Each state (FULL, PARTIAL, ORIGINAL) has a predefined message template.
    // - The messages depend on the relationship between GWP and Free Shipping thresholds and their exists.
    // - The `messageTemplates` object maps each state and condition to a specific message.
    //
    // Helper methods:
    // - this._calculateFreeShippingRemaining(): Computes the remaining amount needed to qualify for Free Shipping.
    // - this._calculateGWPRemaining(): Computes the remaining amount needed to qualify for GWP.

    const STATE = {
        FULL: 'full',
        PARTIAL: 'partial',
        ORIGINAL: 'original'
    };

    const STATUS_MAP = {
        [STATE.FULL]: 2,
        [STATE.PARTIAL]: 1,
        [STATE.ORIGINAL]: 0
    };

    const messageTemplates = {
        [STATE.FULL]: {
            gwpLarger: __NOVA_GLOBAL__.threshold_full_state,
            freeShippingLarger: __NOVA_GLOBAL__.threshold_full_state,
            equalThreshold: __NOVA_GLOBAL__.threshold_full_state,
            gwpOnly: __NOVA_GLOBAL__.threshold_full_state_gwp_only,
            freeShippingOnly: __NOVA_GLOBAL__.threshold_full_state_free_shipping_only
        },
        [STATE.PARTIAL]: {
            gwpLarger: __NOVA_GLOBAL__.threshold_partial_state_shipping_full,
            freeShippingLarger: __NOVA_GLOBAL__.threshold_partial_state_gwp_full
        },
        [STATE.ORIGINAL]: {
            gwpLarger: __NOVA_GLOBAL__.threshold_original_state,
            freeShippingLarger: __NOVA_GLOBAL__.threshold_original_state,
            equalThreshold: __NOVA_GLOBAL__.threshold_original_state,
            gwpOnly: __NOVA_GLOBAL__.threshold_original_state_gwp_only,
            freeShippingOnly: __NOVA_GLOBAL__.threshold_original_state_free_shipping_only
        }
    };

    const determineState = () => {
        if (this.cartSubtotal >= this.largerThreshold) {
            return STATE.FULL;
        }
        if (
            this.cartSubtotal >= this.smallerThreshold &&
            (this.caseType === 'gwpLarger' || this.caseType === 'freeShippingLarger')
        ) {
            return STATE.PARTIAL;
        }
        return STATE.ORIGINAL;
    };

    const currentState = determineState();
    this.status = STATUS_MAP[currentState];

    let message = messageTemplates[currentState][this.caseType] || '';

    message = message
        .replace('{{ free_shipping_more }}', this._calculateFreeShippingRemaining())
        .replace('{{ gwp_more }}', this._calculateGWPRemaining());

    return message;
  }

  render() {
    // NOTE: Show "circle marks" when only set "GWP" and "Free Shipping"
    return html`
      <div class="container">
        <p>${this._displayMessage()}</p>
        <sl-progress-bar
          value=${this.percentageValue}
          aria-label="${window.cartStrings.freeShippingProgress}"
        ></sl-progress-bar>
        ${this.caseType === 'freeShippingLarger' || this.caseType === 'gwpLarger'
          ? html `<span
              class="free-shipping__threshold mark"
              status="${this.status}"
              style="left:${this.percentageValueFreeShipping}%;"
            ></span>
            <span class="gwp__threshold mark" status="${this.status}"></span>`
          : nothing
        }
      </div>
    `
  }

  static styles = [
    styles.flex,
    css`
      :host {
        display: block;
        padding: var(--nova-space-md) 0;
        border-bottom: 1px solid var(--nova-color-brand-taupe) !important;
      }

      .container {
        margin: 0 var(--nova-space-md);
        position: relative;
      }

      @media screen and (min-width: 768px) {
        :host {
          padding: var(--nova-space-sm) 0;
        }

        .container {
          margin: 0 var(--nova-space-sm);
        }
      }

      p {
        margin: 0 0 10px;
        line-height: normal;
      }

      sl-progress-bar {
        --height: 10px;
        --track-color: #fff;
        --indicator-color: var(--nova-color-brand-charcoal);
      }

      sl-progress-bar::part(indicator) {
        border-radius: var(--sl-border-radius-pill);
        background-color: var(--nova-color-brand-charcoal);
      }

      .mark {
        position: absolute;
        bottom: -3px;
        width: 16px;
        height: 16px;
        border-radius: 16px;
        background: var(--nova-color-brand-reverse);
      }

      .free-shipping__threshold {
        transform: translateX(-50%);
      }

      .free-shipping__threshold[status="1"],
      .free-shipping__threshold[status="2"] {
        background: var(--nova-color-brand-charcoal);
      }
      .gwp__threshold {
        right: 0;
      }
      .gwp__threshold[status="2"] {
        background: var(--nova-color-brand-charcoal);
      }
    `,
  ]
}

customElements.define('nova-free-shipping-threshold', NovaFreeShippingThreshold)
